import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Video, Send, Copy, ExternalLink, RefreshCw, Play } from 'lucide-react';
import { theme, cardStyle, inputStyle } from '../theme';

export default function TeleMedPage({ API_BASE }) {
  const [appts, setAppts]   = useState([]);
  const [allAppts, setAll]  = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API_BASE}/telemed/upcoming`),
      axios.get(`${API_BASE}/appointments`),
    ]);
    setAppts(a.data || []);
    setAll((b.data || []).filter(x => !x.telemedEnabled));
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const enable = async (apptId) => {
    const r = await axios.post(`${API_BASE}/telemed/enable/${apptId}`);
    load();
    if (window.confirm('TeleMed enabled. Send link to patient via WhatsApp?')) {
      await axios.post(`${API_BASE}/telemed/send/${apptId}`).catch(e=>alert(e.response?.data?.error || e.message));
    }
  };

  const sendLink = async (apptId) => {
    try {
      const r = await axios.post(`${API_BASE}/telemed/send/${apptId}`);
      alert(r.data.ok ? '✅ Sent on WhatsApp!' : '⚠️ ' + JSON.stringify(r.data.result));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const copy = (link) => { navigator.clipboard.writeText(link); alert('🔗 Link copied!'); };

  const filteredCandidates = allAppts.filter(a =>
    !filter || (a.patientName||'').toLowerCase().includes(filter.toLowerCase()) || (a.phone||'').includes(filter)
  ).slice(0, 20);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>📹 TeleMed — Video Consultations</h2>
        <button onClick={load} style={btn(theme.primary)}><RefreshCw size={14}/> Reload</button>
      </div>

      <div style={{ ...cardStyle, padding: 14, marginBottom: 16, background: theme.secondaryLight }}>
        <div style={{ fontSize: 13, color: theme.navy, lineHeight: 1.6 }}>
          <strong>Free, no-setup video calls</strong> via Jitsi Meet. Click "Enable TeleMed" on any appointment, then "Send Link" — patient ko WhatsApp pe one-click join link mil jaayega. Doctor "Join Room" se enter karta hai.
        </div>
      </div>

      {activeRoom && (
        <div style={{ ...cardStyle, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: 12, background: theme.navy, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>📹 In session: {activeRoom.name}</strong>
            <button onClick={()=>setActiveRoom(null)} style={{ background: theme.danger, color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>End Session</button>
          </div>
          <iframe src={activeRoom.link} title="Jitsi" style={{ width: '100%', height: 520, border: 0 }} allow="camera; microphone; fullscreen; display-capture"/>
        </div>
      )}

      <h3 style={{ margin: '20px 0 10px', color: theme.navy }}>🟢 Upcoming TeleMed Appointments</h3>
      {appts.length === 0 ? (
        <div style={{ ...cardStyle, padding: 30, textAlign: 'center', color: theme.textMuted }}>
          <Video size={32} style={{ opacity: 0.4 }}/>
          <div style={{ marginTop: 8, fontSize: 13 }}>No TeleMed appointments scheduled.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {appts.map(a => (
            <div key={a._id} style={{ ...cardStyle, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{a.patientName}</strong>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>📅 {a.date} · ⏰ {a.time}</div>
                  {a.phone && <div style={{ fontSize: 12, color: theme.textMuted }}>📱 {a.phone}</div>}
                </div>
                <Video size={20} color={theme.success}/>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                <button onClick={()=>setActiveRoom({ link: a.telemedLink, name: a.patientName })} style={{ ...btn(theme.success), flex: 1 }}><Play size={12}/> Join</button>
                <button onClick={()=>sendLink(a._id)} style={btn(theme.secondary)} title="Resend WhatsApp link"><Send size={12}/></button>
                <button onClick={()=>copy(a.telemedLink)} style={btn(theme.textMuted)} title="Copy link"><Copy size={12}/></button>
                <button onClick={()=>window.open(a.telemedLink, '_blank')} style={btn(theme.navy)} title="Open in new tab"><ExternalLink size={12}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h3 style={{ margin: '24px 0 10px', color: theme.navy }}>➕ Enable TeleMed for an Appointment</h3>
      <input value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Search patient name / phone..." style={{ ...inputStyle, marginBottom: 10 }}/>
      <div style={{ ...cardStyle, padding: 0, maxHeight: 360, overflowY: 'auto' }}>
        {filteredCandidates.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>No appointments available to enable.</div>
        ) : filteredCandidates.map(a => (
          <div key={a._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderBottom: `1px solid ${theme.borderLight}` }}>
            <div>
              <strong style={{ fontSize: 13 }}>{a.patientName}</strong>
              <div style={{ fontSize: 11, color: theme.textMuted }}>📅 {a.date} ⏰ {a.time} · {a.treatment || 'Consultation'}</div>
            </div>
            <button onClick={()=>enable(a._id)} style={btn(theme.primary)}><Video size={12}/> Enable TeleMed</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 12px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 12 });
